/**
 * Generic finite-state machine with statically declared transition tables.
 * Lightweight (~50 LOC) replacement for XState — see TD-001 §4.7.2 / PRD-C TR2.
 */
export class FSM<S extends string, E extends string> {
  private state: S;

  constructor(
    initial: S,
    private readonly transitions: Readonly<Record<S, Partial<Record<E, S>>>>,
    private readonly onTransition?: (from: S, event: E, to: S) => void,
  ) {
    this.state = initial;
  }

  /** Current state of the machine. */
  get current(): S {
    return this.state;
  }

  /** Returns true if `event` triggers a transition from the current state. */
  canSend(event: E): boolean {
    return this.transitions[this.state]?.[event] !== undefined;
  }

  /** Attempts to apply `event`. Returns true on successful transition. */
  send(event: E): boolean {
    const next = this.transitions[this.state]?.[event];
    if (next === undefined) return false;
    const prev = this.state;
    this.state = next;
    this.onTransition?.(prev, event, next);
    return true;
  }
}
